import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';

export type LocationFiltersRecord = {
  voicemailBoxesIndex: string[];
  callQueuesIndex: string[];
  callGroupsIndex: string[];
  phoneTreesIndex: string[];
  callRoutesIndex: string[];
  phoneNumbersIndex: string[];
  overridesIndex: string[];
  phoneHoursIndex: string[];
};

export type SettingsTenantLocation = {
  id: string;
  name: string;
  locationType: 'databeta' | 'unify' | 'single' | null;
  phoneTenantId: string;
  children: SettingsTenantLocation[];
  locationId: string;
  orgId: string;
  type: 'group' | 'individual';
};

export type PhoneSettingsStore = {
  settingsTenantLocation: SettingsTenantLocation | undefined;
  setSettingsTenantLocation: (location?: SettingsTenantLocation) => void;
  globalAvailableLocationIds: string[] | undefined;
  setGlobalAvailableLocationIds: (ids: string[]) => void;
  openTenantPicker: () => void;
  closeTenantPicker: () => void;
  setOpenTenantPicker: (fn: () => void) => void;
  setCloseTenantPicker: (fn: () => void) => void;

  phoneTenantId?: string;
  associatedLocationIds?: string[];
  hasMultipleTenantOptions?: boolean;
  tenantLabel?: string;

  setPhoneTenantId: (id: string) => void;
  setAssociatedLocationIds: (ids: string[]) => void;
  setHasMultipleTenantOptions: (hasMultiple: boolean) => void;
  setTenantLabel: (label: string) => void;
};

export const usePhoneSettingsStore = createStoreWithSubscribe<PhoneSettingsStore>(
  (set) => ({
    settingsTenantLocation: undefined,
    globalAvailableLocationIds: undefined,
    setSettingsTenantLocation: (location) => {
      set({ settingsTenantLocation: location });
    },
    setGlobalAvailableLocationIds: (locationIds) => {
      set({ globalAvailableLocationIds: locationIds });
    },
    openTenantPicker: () => {
      console.error('openTenantPicker not set');
    },
    closeTenantPicker: () => {
      console.error('closeTenantPicker not set');
    },
    setOpenTenantPicker: (fn) => {
      set({ openTenantPicker: fn });
    },
    setCloseTenantPicker: (fn) => {
      set({ closeTenantPicker: fn });
    },
    phoneTenantId: undefined,
    /**
     * What locations are associated with the selected tenant?
     *
     * This list contains the location IDs that belong to the selected tenant location,
     * pre-filtered to only include locations that are also in appScopeStore's selectedLocationIds.
     *
     * Example scenario:
     * - Tenant Location A (Unify Group, Tenant A) has children: Location 1, Location 2, Location 3
     * - Tenant Location B (Databeta Location, Tenant B) is a single location
     *
     * If user selects Tenant Location A and appScopeStore.selectedLocationIds = ['loc1', 'loc2', 'loc3']:
     *   → associatedLocationIds = ['loc1', 'loc2', 'loc3']
     *
     * If user selects Tenant Location A and appScopeStore.selectedLocationIds = ['loc2']:
     *   → associatedLocationIds = ['loc2'] (only the intersection)
     *
     * If user selects Tenant Location B (single databeta location):
     *   → associatedLocationIds = ['loc-b']
     *
     * Note: The filtering happens when building tenantLocationOptions.
     */
    associatedLocationIds: undefined,
    associatedGroupId: undefined,
    locationType: undefined,
    hasMultipleTenantOptions: undefined,
    tenantLabel: undefined,
    setPhoneTenantId: (id) => {
      set({ phoneTenantId: id });
    },
    setAssociatedLocationIds: (ids) => {
      set({ associatedLocationIds: ids });
    },
    setHasMultipleTenantOptions: (hasMultiple) => {
      set({ hasMultipleTenantOptions: hasMultiple });
    },
    setTenantLabel: (label) => {
      set({ tenantLabel: label });
    },
  }),
  {
    name: 'PhoneSettingsStore',
    trace: true,
  }
);

export const usePhoneSettingsShallowStore = createShallowStore<PhoneSettingsStore>(usePhoneSettingsStore);

export type PhoneLocationFilterStore = {
  locationFilters: LocationFiltersRecord;
  setLocationFilters: (filters: Partial<LocationFiltersRecord>) => void;
};

export const usePhoneLocationFilterStore = createStoreWithSubscribe<PhoneLocationFilterStore>(
  (set) => ({
    locationFilters: {
      voicemailBoxesIndex: [],
      callQueuesIndex: [],
      callGroupsIndex: [],
      phoneTreesIndex: [],
      callRoutesIndex: [],
      phoneNumbersIndex: [],
      overridesIndex: [],
      phoneHoursIndex: [],
    },
    setLocationFilters: (filters) => {
      set((state) => ({ locationFilters: { ...state.locationFilters, ...filters } }));
    },
  }),
  {
    name: 'PhoneLocationFilterStore',
    trace: true,
  }
);

export const usePhoneLocationFilterShallowStore =
  createShallowStore<PhoneLocationFilterStore>(usePhoneLocationFilterStore);
